import * as React from 'react'
import { FC } from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import { IPath, ROUTE_PATH } from '../../utils/constants'
import ColoredSVG from '../ColoredSVG/ColoredSVG'
import { cls } from '../../utils/utils'
import * as styles from './Footer.module.scss'
import { IFooterData, IFooterTemplateData } from './Footer.types'

export const FooterTemplate: FC<IFooterTemplateData> = ({ socials, sites, flags, copyright, companies }) => {
  return (
    <footer className={styles.container}>
      <section className={cls('main-container', styles.content)}>
        <nav className={styles.connect}>
          <div className={styles.inner}>
            <h3>CONNECT</h3>
            {socials.map((row, index) => (
              <ul className={cls(styles.links, 'mobile-hide')} key={index}>
                {row.row.map((social) => (
                  <li key={social.title} className={cls(styles.link, social.isWide ? styles.wideLink : styles.normalLink)}>
                    <a href={social.url} target="_blank">
                      <ColoredSVG url={social.icon?.publicURL} width={social.isWide ? 75 : 50} height={50} />
                    </a>
                  </li>
                ))}
              </ul>
            ))}

            <div className={cls(styles.socialsContainer, 'desktop-hide')}>
              {socials.map((row, index) => (
                <React.Fragment key={index}>
                  {row.row.map((social) => (
                    <div key={social.title} className={styles.link}>
                      <a href={social.url} target="_blank">
                        <ColoredSVG url={social.icon?.publicURL} width={social.isWide ? 75 : 50} height={50} />
                      </a>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>
        <nav className={styles.explore}>
          <div className={styles.inner}>
            <h3>EXPLORE</h3>
            <ul className={styles.links}>{renderLinks()}</ul>
          </div>
        </nav>
        <nav className={styles.sites}>
          <div className={styles.inner}>
            <h3>OUR SITES</h3>
            <ul className={styles.links}>
              {sites.map((site) => (
                <li key={site.title} className="text--small">
                  <a className={styles.link} href={site.url} target="_blank">
                    {site.text}
                  </a>
                </li>
              ))}
              <li>
                <div className={styles.flags}>
                  {flags.map((flag) => (
                    <a href={flag.url} className={styles.link} key={flag.url} target="_blank">
                      <ColoredSVG url={flag.icon?.publicURL} className={styles.flag} />
                    </a>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </nav>
        <article className={styles.copyright}>
          <div className={styles.inner}>
            <span className="text--small">{copyright + `${new Date().getFullYear()}`}</span>
            <Link
              className={cls('text--small', styles.link)}
              to="/privacy-policy"
              replace={typeof location !== `undefined` && location.pathname === '/privacy-policy'}
            >
              Privacy Policy
            </Link>
          </div>
        </article>
        <nav className={styles.companies}>
          {companies.map((company) => (
            <div key={company.title} className={styles.company}>
              <div className={styles.icon}>
                <a className={styles.url} href={company.url} target="_blank">
                  <ColoredSVG url={company.icon?.publicURL} maxHeight={50} />
                </a>
              </div>
              <span className="text--caption">{company.title}</span>
            </div>
          ))}
        </nav>
      </section>
    </footer>
  )
}

const Footer = () => {
  const footerData: IFooterData = useStaticQuery(query)
  const data: IFooterTemplateData = footerData.markdownRemark.frontmatter
  return <FooterTemplate {...data} />
}

const renderLinks = () => {
  return Object.keys(ROUTE_PATH).map((key) => {
    const path = ROUTE_PATH[key as IPath].path
    return (
      <li key={key}>
        <Link className={cls('text--small', styles.link)} to={path} replace={typeof location !== `undefined` && location.pathname === path}>
          {ROUTE_PATH[key as IPath].name}
        </Link>
      </li>
    )
  })
}

export const query = graphql`
  query Footer {
    markdownRemark(frontmatter: { componentName: { eq: "Footer" } }) {
      frontmatter {
        socials {
          row {
            title
            icon {
              publicURL
            }
            url
            isWide
          }
        }
        sites {
          title
          text
          url
        }
        flags {
          title
          url
          icon {
            publicURL
          }
        }
        copyright
        companies {
          title
          icon {
            publicURL
          }
          url
        }
      }
    }
  }
`

export default Footer
